import { MouseEvent, useCallback, useContext, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { noopFunction } from '@vestico/utils';

import { ProductBase, WidgetButtonEntry, getTitleForVariantOptions, isBeautyMatchingCategory } from '@vestico/model';

import { CSSPropertiesFromString, GlobalsContext } from '@vestico/shared';

import { ProductContext } from '../../MainContext';
import { ConfigContext, MatchButtonGlobalsContext } from '../../WidgetEventsManager';
import { MatchContext, MatchResponsesContext } from '../matching/MatchContext';
import { MatchModalLazyLoader } from '../matching/MatchModalLazyLoader';
import { useShowEntry } from './UseShowEntry';

export interface ButtonInjectionProps {
  entry: WidgetButtonEntry;
  element: HTMLElement;
}

interface InternalButtonInjectionProps extends ButtonInjectionProps {
  useInlineSizeRecommendation: boolean;
  product: ProductBase | null | undefined;
}

const getButtonForElement = (element: HTMLElement) => {
  // Add custom styles from the entry config
  const button = element.getElementsByClassName('vw-fit-match-button').item(0) as HTMLButtonElement | undefined;
  return button;
};

const ButtonInjectionInternal = ({ entry, element, product, useInlineSizeRecommendation }: InternalButtonInjectionProps) => {
  const { cssClasses, cssStyle, id, useAlternativeButtonTitle, isThemeInserted } = entry;

  const { setMatchButtonOpen } = useContext(MatchButtonGlobalsContext);
  const { sizeRecommendation } = useContext(MatchResponsesContext);

  const { onSetActiveProduct } = useContext(ProductContext);
  const sizeRecommendations = useMemo(() => sizeRecommendation?.recommendations ?? undefined, [sizeRecommendation]);

  const { showEntry } = useShowEntry({ entry });
  const { isQuiz } = useContext(ConfigContext);

  const modal = useMemo(() => <MatchModalLazyLoader />, []);

  const { t } = useTranslation();

  const runOpenWidget = useCallback(() => {
    const isQuizElement = element.getAttribute('vw-mode') === 'quiz';
    console.warn('vestico: runOpenWidget', JSON.stringify({ isQuizElement, productUID: product?.uid }, null, 2));
    onSetActiveProduct(isQuizElement ? undefined : product?.uid);
    setMatchButtonOpen(true, id);
    return false;
  }, [product, element, setMatchButtonOpen, id, onSetActiveProduct]);

  const openWidget = useCallback(
    (e: Event) => {
      e.preventDefault();
      return runOpenWidget();
    },
    [runOpenWidget]
  );

  const handleOpenWidget = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      return runOpenWidget();
    },
    [runOpenWidget]
  );

  const useInlineSizeRecButtonTitle = useMemo(
    () =>
      useInlineSizeRecommendation && sizeRecommendations && sizeRecommendations.length > 0 && !isBeautyMatchingCategory(product?.matchingCategory),
    [product?.matchingCategory, sizeRecommendations, useInlineSizeRecommendation]
  );

  const buttonTitle = useMemo(() => {
    if (isBeautyMatchingCategory(product?.matchingCategory)) {
      return t('LOC_BEAUTY_MATCH_BUTTON_TITLE');
    }

    if (useInlineSizeRecButtonTitle && sizeRecommendations) {
      const firstSizeRec = sizeRecommendations[0];
      return t('LOC_FIT_MATCH_BUTTON_TITLE_INLINE', { size: getTitleForVariantOptions(firstSizeRec.variantOptions) });
    }

    return useAlternativeButtonTitle ? t('LOC_WIDGET_INFO_DESCRIPTION_SIZE_REC') : t('LOC_FIT_MATCH_BUTTON_TITLE');
  }, [product?.matchingCategory, sizeRecommendations, t, useAlternativeButtonTitle, useInlineSizeRecButtonTitle]);

  const cssProperties = useMemo(() => CSSPropertiesFromString(cssStyle), [cssStyle]);

  useLayoutEffect(() => {
    // Theme inserted entries already render the insertion point
    // We need to make sure we hide/unhide accordingly and apply styles
    if (element) {
      const showElement = showEntry || !!isQuiz;
      const displayString = showElement ? 'block' : 'none';

      // Unhide the container element
      element.style.display = displayString;

      // Add custom styles from the entry config
      const button = getButtonForElement(element);
      if (!button) {
        console.error(`Missing button in static element for entry ${entry.id}`);
        return noopFunction;
      }

      button.addEventListener('click', openWidget);

      if (cssClasses && cssClasses.length > 0) {
        button.classList.toggle(cssClasses, true);
      }

      if (cssStyle) {
        const existingStyle = button.getAttribute('style');

        // Only insert styles the first time
        if (existingStyle && !existingStyle.includes(cssStyle)) {
          const newStyle = `${existingStyle ? `${existingStyle};` : ''}${cssStyle}`;
          button.setAttribute('style', newStyle);
        }
      }

      return () => {
        button.removeEventListener('click', openWidget);
      };
    }
    return noopFunction;
  }, [cssClasses, cssStyle, element, entry, isQuiz, isThemeInserted, openWidget, showEntry]);

  useLayoutEffect(() => {
    if (element && useInlineSizeRecButtonTitle) {
      const button = getButtonForElement(element);
      if (!button) {
        return;
      }
      button.innerText = buttonTitle;
    }
  }, [buttonTitle, element, entry, isThemeInserted, showEntry, useInlineSizeRecButtonTitle]);

  if (!showEntry && !isQuiz) {
    return null;
  }

  if (isThemeInserted) {
    return modal;
  }

  return (
    <>
      <button type="button" className={`vestico-widget ${cssClasses ?? ''}`} onClick={handleOpenWidget} style={cssProperties}>
        <span>{buttonTitle}</span>
      </button>
      {modal}
    </>
  );
};

export const ButtonInjection = ({ entry, element }: ButtonInjectionProps) => {
  const { stylesConfig } = useContext(GlobalsContext);
  const { useInlineSizeRecommendation } = stylesConfig.stylesConfig;

  const { showEntry, availableMatchingTypes } = useShowEntry({ entry });
  const { allProducts, defaultProduct } = useContext(ProductContext);
  const taggedProductUID = useMemo(() => element.children.item(0)?.getAttribute('vw-product-id'), [element]);

  const currentProduct = useMemo(() => {
    return taggedProductUID ? allProducts?.[taggedProductUID]?.product : defaultProduct;
  }, [taggedProductUID, allProducts, defaultProduct]);

  if (!showEntry) {
    return null;
  }

  return (
    <MatchContext
      availableMatchingTypes={availableMatchingTypes}
      useInlineSizeRecommendation={!!useInlineSizeRecommendation}
      product={currentProduct}
    >
      <ButtonInjectionInternal entry={entry} element={element} product={currentProduct} useInlineSizeRecommendation={!!useInlineSizeRecommendation} />
    </MatchContext>
  );
};

export default ButtonInjection;
